import type { Chart } from "../Chart";
import { Theme } from "../theme";

// Draw candlesticks for the visible index slice. Reads straight from the
// timeframe store's typed arrays — no per-bar object allocation.
export function drawCandles(ctx: CanvasRenderingContext2D, chart: Chart) {
  const data = chart.data;
  if (!data) return;
  const vp = chart.viewport;
  const [lo, hi] = vp.visibleRange();
  if (hi <= lo) return;

  const spacing = vp.barSpacing;
  const bodyW = Math.max(1, Math.floor(spacing * 0.72));
  const halfBody = bodyW / 2;
  const thin = spacing < 3; // draw as OHLC bars / lines when too dense
  const wickW = spacing >= 6 ? Math.max(1, Math.round(spacing * 0.12)) : 1;

  // clip to plot area
  ctx.save();
  ctx.beginPath();
  ctx.rect(0, 0, vp.plot.width, vp.plot.height);
  ctx.clip();

  // pass split by direction to minimize fillStyle changes
  for (let pass = 0; pass < 2; pass++) {
    const bull = pass === 0;
    ctx.fillStyle = bull ? Theme.bull : Theme.bear;
    ctx.beginPath();
    for (let i = lo; i < hi; i++) {
      const o = data.openAt(i);
      if (!isFinite(o)) continue;
      const c = data.closeAt(i);
      const isBull = c >= o;
      if (isBull !== bull) continue;
      const h = data.highAt(i);
      const l = data.lowAt(i);
      const xc = vp.xCenterOfIndex(i);
      const yH = vp.yOfPrice(h);
      const yL = vp.yOfPrice(l);
      // wick
      const wx = Math.round(xc - wickW / 2);
      ctx.rect(wx, yH, wickW, Math.max(1, yL - yH));
      if (!thin) {
        const yO = vp.yOfPrice(o);
        const yC = vp.yOfPrice(c);
        const top = Math.min(yO, yC);
        const bh = Math.max(1, Math.abs(yC - yO));
        const bx = Math.round(xc - halfBody);
        ctx.rect(bx, top, bodyW, bh);
      }
    }
    ctx.fill();
  }
  ctx.restore();
}

// Overlaid translucent volume histogram along the bottom of the plot.
export function drawVolume(ctx: CanvasRenderingContext2D, chart: Chart) {
  const data = chart.data;
  if (!data) return;
  const vp = chart.viewport;
  const [lo, hi] = vp.visibleRange();
  if (hi <= lo) return;

  let maxVol = 0;
  for (let i = lo; i < hi; i++) {
    const v = data.volumeAt(i);
    if (isFinite(v) && v > maxVol) maxVol = v;
  }
  if (maxVol <= 0) return;

  const paneH = vp.plot.height * 0.16;
  const baseY = vp.plot.height;
  const spacing = vp.barSpacing;
  const bodyW = Math.max(1, Math.floor(spacing * 0.72));
  const halfBody = bodyW / 2;

  ctx.save();
  ctx.beginPath();
  ctx.rect(0, 0, vp.plot.width, vp.plot.height);
  ctx.clip();
  for (let pass = 0; pass < 2; pass++) {
    const bull = pass === 0;
    ctx.fillStyle = bull ? Theme.volumeBull : Theme.volumeBear;
    ctx.beginPath();
    for (let i = lo; i < hi; i++) {
      const o = data.openAt(i);
      if (!isFinite(o)) continue;
      const c = data.closeAt(i);
      if ((c >= o) !== bull) continue;
      const v = data.volumeAt(i);
      if (!isFinite(v) || v <= 0) continue;
      const h = (v / maxVol) * paneH;
      const xc = vp.xCenterOfIndex(i);
      const bx = Math.round(xc - halfBody);
      ctx.rect(bx, baseY - h, bodyW, h);
    }
    ctx.fill();
  }
  ctx.restore();
}
